import { CheckIcon } from '@primer/octicons-react';
import { Suspense } from 'react';
import { graphql, useFragment } from 'react-relay';

import { BookScrapUpdateDialog_bookScrap$key } from '../../../relay/__generated__/BookScrapUpdateDialog_bookScrap.graphql';
import { BookScrapUpdateDialog_bookScrapUpdateMutation } from '../../../relay/__generated__/BookScrapUpdateDialog_bookScrapUpdateMutation.graphql';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import EnumPairSelectField from '../../core/EnumPairSelectField';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import KatexEditorField from '../../core/KatexEditorField';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import View from '../../core/View';
import UnitDSelectDialogField from '../../unitD/UnitDSelectDialogField';

const BookScrapUpdateDialog_bookScrap = graphql`
  fragment BookScrapUpdateDialog_bookScrap on BookScrap {
    id
    unitD {
      id
    }
    difficultyLevel
    problemBookPage
    problemNumber
    problemTex
    solutionBookPage
    solutionTex
  }
`;

type Props = { bookScrap: BookScrapUpdateDialog_bookScrap$key } & DialogProps &
  Pick<
    MutationFormikProps<
      BookScrapUpdateDialog_bookScrapUpdateMutation,
      { problem: { text: string | null }; solution: { text: string | null } }
    >,
    'onSubmit' | 'config' | 'connections'
  >;

const BookScrapUpdateDialog = ({ bookScrap, onSubmit, config, connections, ...props }: Props) => {
  const { id, unitD, difficultyLevel, problemBookPage, problemNumber, problemTex, solutionBookPage, solutionTex } =
    useFragment(BookScrapUpdateDialog_bookScrap, bookScrap);

  return (
    <Dialog wide {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>수정하기</Text>
      </Dialog.Header>
      <MutationFormik<
        BookScrapUpdateDialog_bookScrapUpdateMutation,
        { problem: { text: string | null }; solution: { text: string | null } }
      >
        mutation={graphql`
          mutation BookScrapUpdateDialog_bookScrapUpdateMutation($input: BookScrapUpdateInput!) {
            bookScrapUpdate(input: $input) {
              id
              ...BookScrapUpdateDialog_bookScrap
            }
          }
        `}
        initialValues={{
          id,
          unitD: unitD?.id,
          difficultyLevel,
          problemBookPage,
          problemNumber,
          solutionBookPage,
          problem: { text: problemTex },
          solution: { text: solutionTex },
        }}
        onSubmit={onSubmit}
        config={config}
        connections={connections}
        valuesTransformer={({ problem, solution, ...values }) => ({
          ...values,
          problemTex: problem.text,
          solutionTex: solution.text,
        })}
      >
        {({ handleSubmit }, { nonFieldErrorRef, scrollContainerRef }) => (
          <>
            <Dialog.Body ref={scrollContainerRef}>
              <FormLayout>
                <NonFieldError ref={nonFieldErrorRef} />
                <Suspense>
                  <UnitDSelectDialogField name={'unitD'} label={'Unit D'} placeholder={'Unit D 선택'} required />
                </Suspense>
                <Suspense>
                  <EnumPairSelectField
                    typename={'ProblemDifficultyLevelEnum'}
                    name={'difficultyLevel'}
                    label={'난이도'}
                    required
                    transformer={(enumPairs) => enumPairs.toReversed()}
                  />
                </Suspense>
                <View>
                  <Grid gapX={1} sx={{ alignItems: 'center' }}>
                    <Grid.Unit size={'max'}>
                      <Text>문제</Text>
                    </Grid.Unit>
                    <Grid.Unit size={'min'}>
                      <View display={'flex'} alignItems={'center'} sx={{ columnGap: 1 }}>
                        <NumberField
                          sx={{ width: 88 }}
                          label={'문제 책 페이지'}
                          labelConfig={{ visuallyHidden: true }}
                          name={'problemBookPage'}
                          min={1}
                          required
                        />
                        <Text fontSize={1}>P</Text>
                        <TextField
                          sx={{ width: 88 }}
                          label={'문제 번호'}
                          labelConfig={{ visuallyHidden: true }}
                          placeholder={'문제 번호'}
                          name={'problemNumber'}
                          required
                        />
                      </View>
                    </Grid.Unit>
                  </Grid>
                  <View mt={3}>
                    <KatexEditorField label={'문제'} labelConfig={{ visuallyHidden: true }} name={'problem'} reverse />
                  </View>
                </View>
                <View>
                  <Grid gapX={1} sx={{ alignItems: 'center' }}>
                    <Grid.Unit size={'max'}>
                      <Text>해설</Text>
                    </Grid.Unit>
                    <Grid.Unit size={'min'}>
                      <View display={'flex'} alignItems={'center'} sx={{ columnGap: 1 }}>
                        <NumberField
                          sx={{ width: 88 }}
                          label={'해설 책 페이지'}
                          labelConfig={{ visuallyHidden: true }}
                          name={'solutionBookPage'}
                          min={1}
                          required
                        />
                        <Text fontSize={1}>P</Text>
                      </View>
                    </Grid.Unit>
                  </Grid>
                  <View mt={3}>
                    <KatexEditorField label={'해설'} labelConfig={{ visuallyHidden: true }} name={'solution'} reverse />
                  </View>
                </View>
              </FormLayout>
            </Dialog.Body>
            <Dialog.Footer>
              <Button leadingIcon={CheckIcon} variant={'primary'} onClick={() => handleSubmit()}>
                수정하기
              </Button>
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default BookScrapUpdateDialog;
export type { Props as BookScrapUpdateDialog };
